import { Action, getModule, Module, Mutation, VuexModule } from "vuex-module-decorators";
import store from "@/store";
import suppliersClient from "../../api/suppliers/SuppliersClient";
import { appModule } from "@/store/modules/moduleApp";
import { Supplier, SupplierConfig } from "@/api/suppliers/SupplierModels";
import { httpModule } from "@/store/modules/moduleHttp";
import { PaymentConfRS } from "@common/models/payment-config-models";
import { extractSupplierContentPolicy } from "@/utils/helpers";

@Module({
  dynamic: true,
  store,
  name: "rdm-rc-supplier-details",
  namespaced: true,
})
class ModuleSuppliers extends VuexModule {
  /* Data */
  private loading = false;
  private supplierDetails = [];
  private supplierID = "";
  private suppliersList: Supplier[] = [];
  private suppliersConfig: SupplierConfig[] = [];
  private supplierSingleConfig: SupplierConfig = {} as SupplierConfig;
  private paymentConf: PaymentConfRS = {} as PaymentConfRS;
  private supplierContentPolicy = "";

  /* Getters */
  get Loading(): boolean {
    return this.loading;
  }

  get SupplierID(): string {
    return this.supplierID;
  }

  get SuppliersList(): Supplier[] {
    return this.suppliersList;
  }

  get SuppliersConfig(): SupplierConfig[] {
    return this.suppliersConfig;
  }

  get SupplierSingleConfig(): SupplierConfig {
    return this.supplierSingleConfig;
  }

  get PaymentsConfig(): PaymentConfRS {
    return this.paymentConf;
  }

  get SupplierContentPolicy(): string {
    return this.supplierContentPolicy;
  }

  get RequiredGuestNamePerTraveler() {
    return this.supplierSingleConfig.configuration.guestDetails.requireGuestNamePerTraveler;
  }

  get RequireGuestEmailPerTraveler() {
    return this.supplierSingleConfig.configuration.guestDetails.requireGuestEmailPerTraveler;
  }

  get RequiredGuestPhonePerTraveler() {
    return this.supplierSingleConfig.configuration.guestDetails.requireGuestPhonePerTraveler;
  }

  get RequiredGuestWeightPerTraveler() {
    return this.supplierSingleConfig.configuration.guestDetails.requireGuestWeightPerTraveler;
  }

  get RequiredGuestCountryPerTraveler() {
    return this.supplierSingleConfig.configuration.guestDetails.requireGuestCountryPerTraveler;
  }

  get BookingConfirmationTemplateID() {
    return this.supplierSingleConfig.configuration.bookingVoucher.bookingConfirmationTemplateId;
  }

  get BookingCancellationTemplateID() {
    return this.supplierSingleConfig.configuration.bookingVoucher.bookingCancellationTemplateId;
  }

  /* Setters */
  @Mutation
  setLoading(loading: boolean) {
    this.loading = loading;
  }

  @Mutation
  setSupplierDetails(details: []) {
    this.supplierDetails = details;
  }

  @Mutation
  setSuppliersList(suppliersList: Supplier[]) {
    this.suppliersList = suppliersList;
  }

  @Mutation
  setSuppliersConfig(suppliersConf: SupplierConfig[]) {
    this.suppliersConfig = suppliersConf;
  }

  @Mutation
  setSuppliersSingleConfig(supplierConf: SupplierConfig) {
    this.supplierSingleConfig = supplierConf;
  }

  @Mutation
  setPaymentsConfig(paymentConf: PaymentConfRS) {
    this.paymentConf = paymentConf;
  }

  @Mutation
  setSupplierContentPolicy(policy: string) {
    this.supplierContentPolicy = policy;
  }

  /* Actions */
  @Action
  startLoading() {
    this.setLoading(true);
  }

  @Action
  findSupplierSingleConfig(id: string) {
    const config = this.suppliersConfig.find((conf: SupplierConfig) => conf.supplierId === id);
    if (config) this.setSuppliersSingleConfig(config);
  }

  @Mutation
  setSupplierID(id: string) {
    this.supplierID = id;
  }

  @Action
  async fetchSupplierById(supplierId: string): Promise<Supplier | null> {
    this.setSupplierID(supplierId);
    await this.setLoading(true);
    try {
      const supplier = await suppliersClient.getSupplierDetails(supplierId, true);
      this.setSupplierDetails(supplier);

      const contentPolicy = extractSupplierContentPolicy(supplier);
      this.setSupplierContentPolicy(contentPolicy);

      return supplier;
    } finally {
      this.setLoading(false);
    }
  }

  @Action
  async getSupplierDetails() {
    await Promise.resolve(this.setLoading(true))
      .then(() => suppliersClient.getSupplierDetails(this.SupplierID))
      .then((details) => this.setSupplierDetails(details))
      .finally(() => this.setLoading(false));
  }

  @Action
  async getSuppliersList() {
    await Promise.resolve(this.setLoading(true))
      .then(() => suppliersClient.getSuppliers())
      .then((suppliers) => {
        suppliers.sort((a: Supplier, b: Supplier) => {
          return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
        });
        this.setSuppliersList(suppliers);
      })
      .then(async () => {
        const supplierIds = this.SuppliersList.map((supplier: Supplier) => supplier.id);
        await httpModule.getSuppliersConfig(supplierIds);
      })
      .catch((error) => {
        const errMsg = error?.response?.data?.error?.message ? `Supplier Error: ${error?.response?.data?.error?.message}` : "Suppliers Load Error";
        appModule.addErrorMsg(errMsg);
      })
      .finally(() => this.setLoading(false));
  }

  @Action
  finishLoading() {
    this.setLoading(false);
  }

  @Action
  clearState() {
    this.setLoading(false);
  }
}

export const moduleSuppliers = getModule(ModuleSuppliers, store);
